import { Alert, Badge, Button, Group, Paper, Stack, Text } from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { useQuery } from "@tanstack/react-query";
import { LoadingOverlay, PageHeader } from "../../components/ui";
import { contractsApi } from "../../lib/api";
import { NAV_COPY, NAV_ICONS } from "../../lib/navCopy";
import { formatDate, formatPaise } from "../../lib/format";

export function HrContractPage() {
  const contractsQuery = useQuery({
    queryKey: ["hr-contracts"],
    queryFn: contractsApi.listHrContracts,
    retry: false,
  });

  if (contractsQuery.isLoading) {
    return <LoadingOverlay height="var(--axio-unit-300)" />;
  }

  const contracts = contractsQuery.data ?? [];

  if (contractsQuery.isError || contracts.length === 0) {
    return (
      <Stack gap="lg">
        <PageHeader
          icon={NAV_ICONS.rateCards}
          title={NAV_COPY.common.rateCards}
        />
        <Alert color="yellow">
          No active rate cards on file. You can save job drafts, but publishing is blocked until admin creates and
          activates a contract.
        </Alert>
      </Stack>
    );
  }

  return (
    <Stack gap="lg" maw="var(--axio-unit-760)">
      <PageHeader
        icon={NAV_ICONS.rateCards}
        title={NAV_COPY.common.rateCards}
        subtitle="Active commercial rate cards for your company. Pick one when posting each job."
      />

      {contracts.map((c) => (
        <Paper key={c.id} withBorder p="lg">
          <Stack gap="xs">
            <Group justify="space-between">
              <Text fw="var(--axio-font-weight-semibold)">{c.name}</Text>
              <Group gap="xs">
                {c.is_default && (
                  <Badge size="sm" variant="outline" color="gray">
                    Default
                  </Badge>
                )}
                <Badge size="sm" color="green">
                  Active
                </Badge>
              </Group>
            </Group>
            <Text size="sm">Fee basis: {c.fee_basis === "flat" ? "Flat fee per hire" : "% of CTC"}</Text>
            <Text size="sm" className="tabular">
              {c.fee_basis === "flat"
                ? `Fee per hire: ${formatPaise(c.fee_flat_paise ?? 0)}`
                : `Fee (% of CTC): ${c.fee_pct_ctc ?? "—"}%`}
            </Text>
            <Text size="sm" className="tabular">
              Guarantee window: {c.guarantee_days} days
            </Text>
            <Text size="sm">Effective from: {formatDate(c.effective_from)}</Text>
            <Text size="sm">Effective to: {c.effective_to ? formatDate(c.effective_to) : "Open-ended"}</Text>
            <Text size="sm">Last updated: {formatDate(c.updated_at)}</Text>
            <Group mt="sm">
              <Button
                variant="default"
                onClick={async () => {
                  try {
                    await contractsApi.downloadHrContractDraftPdf(c.id);
                  } catch (error) {
                    const message = error instanceof Error ? error.message : "Download failed";
                    notifications.show({ color: "red", title: "Failed", message });
                  }
                }}
              >
                Download Draft Contract
              </Button>
              <Button
                variant="default"
                disabled={!c.pdf_filename}
                onClick={async () => {
                  try {
                    await contractsApi.downloadHrContractPdf(c.id);
                  } catch (error) {
                    const message = error instanceof Error ? error.message : "Download failed";
                    notifications.show({ color: "red", title: "Failed", message });
                  }
                }}
              >
                Download Signed PDF
              </Button>
            </Group>
          </Stack>
        </Paper>
      ))}
    </Stack>
  );
}
